import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactQuill from "react-quill";
import pageData from "./pageData.json"; // Your provided JSON
import "react-quill/dist/quill.snow.css";
import { IoArrowBackSharp } from "react-icons/io5";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

const EditAds = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    adType: "google",
    page: "",
    location: "",
    customAdHtml: "",
  });

  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await fetch(`${API_URL}/ads/getAdById/${id}`);
        const data = await res.json();
        if (res.ok) {
          setFormData(data.ad);

          // Set preview image if page and location are valid
          const selectedPage = pageData[data.ad.page];
          if (selectedPage?.locations[data.ad.location]) {
            setImagePreview(selectedPage.locations[data.ad.location]);
          }
        } else {
          alert("Failed to fetch ad");
        }
      } catch (err) {
        console.error(err);
        alert("Error fetching ad");
      }
    };

    fetchAd();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update image preview when page or location changes
    if (name === "page" && pageData[value]?.locations) {
      setImagePreview("");
      setFormData((prev) => ({
        ...prev,
        location: "", // Reset location when page changes
      }));
    } else if (name === "location" && pageData[formData.page]?.locations[value]) {
      setImagePreview(pageData[formData.page].locations[value]);
    }
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      customAdHtml: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/ads/updateAd/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success("Ad updated successfully!");
        navigate("/ads");
      } else {
        const errData = await res.json();
        alert("Error: " + errData.message);
      }
    } catch (err) {
      console.error("Error updating ad:", err);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
     <Link to='/ads'><IoArrowBackSharp />
     </Link>
    <div className="max-w-3xl mx-auto p-4">
      <h2 className="text-xl font-bold mb-4">Edit Ad</h2>
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div>
          <label className="block mb-1">Ad Type</label>
          <select
            name="adType"
            value={formData.adType}
            onChange={handleChange}
            className="w-full border p-2 rounded"
          >
            <option value="google">Google</option>
            <option value="custom">Custom</option>
            <option value="non">None</option>
          </select>
        </div>

        <div>
          <label className="block font-medium mb-1">Page</label>
          <select
            name="page"
            value={formData.page}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
          >
            <option value="">Select Page</option>
            {Object.entries(pageData).map(([key, value]) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        </div>

        {imagePreview && (
          <div>
            <p className="mb-1 text-sm text-gray-600">Preview Image:</p>
            <img
              src={imagePreview}
              alt="Location Preview"
              className="w-full max-w-sm rounded border"
            />
          </div>
        )}

        <div>
          <label className="block font-medium mb-1">Page Location</label>
          <select
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="w-full border p-2 rounded"
            required
            disabled={!formData.page}
          >
            <option value="">Select Page Location</option>
            {formData.page &&
              pageData[formData.page]?.locations &&
              Object.entries(pageData[formData.page].locations).map(
                ([key, location]) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                )
              )}
          </select>
        </div>

        {formData.adType === "custom" && (
          <div>
            <label className="block mb-1">Custom Ad HTML</label>
            <ReactQuill
              value={formData.customAdHtml}
              onChange={handleQuillChange}
              className="bg-white rounded"
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {loading ? "Updating..." : "Update Ad"}
        </button>
      </form>
    </div>
    </>

  );
};

export default EditAds;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "link",
  "image",
];
